/**
 * =============================================================================
 * Componente: FunnelStrip
 * Versão: 4.0.0 — Funil 3D pastel com perspectiva (anéis + elipses)
 *
 * Descrição:
 *   KPI premium de 3 etapas (Cliques → Leads → Ganhos) renderizado como
 *   um funil 3D real, usando elipses no topo de cada anel para criar
 *   sensação de profundidade e perspectiva — não mais trapézios chapados.
 *
 *   - Paleta pastel suave (lavanda → índigo pastel → menta), com degradê
 *     vertical em cada anel + highlight lateral para reforçar volume.
 *   - Largura de cada anel é proporcional ao valor da etapa.
 *   - Métricas internas: label CAPS pequena + valor grande tabular.
 *   - Retenção entre etapas mostrada em texto puro (↓ 67.7%) à direita,
 *     sem pílulas pretas — tipografia editorial, alta legibilidade.
 *   - "Convertidos" renomeado para "Ganhos" (cabe melhor visualmente).
 *   - Sem libs externas, SVG puro, leve.
 *
 * Compactação:
 *   Altura reduzida para caber lado a lado com QuickActionsRow no desktop
 *   (grid 1/2 + 1/2). No mobile mantém boa proporção.
 * =============================================================================
 */
import { Skeleton } from '@/components/ui/skeleton';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';

/** Métricas do funil com drill-down disponível (v4.1.0). */
export type FunnelMetric = 'referrals' | 'conversions';

interface FunnelStripProps {
  isLoading?: boolean;
  clicks: number;
  leads: number;
  conversions: number;
  conversionRate: number;
  /** v4.1.0 — Drill-down ao clicar nos anéis "Indicações" e "Ganhos". */
  onMetricClick?: (metric: FunnelMetric) => void;
}


/** Formata inteiro em pt-BR. */
function fmtInt(v: number): string {
  return v.toLocaleString('pt-BR');
}

/** Calcula porcentagem segura entre etapas (evita divisão por zero). */
function ratio(curr: number, prev: number): number {
  if (!prev || prev <= 0) return 0;
  return Math.min(100, (curr / prev) * 100);
}

export function FunnelStrip({
  isLoading,
  clicks,
  leads,
  conversions,
  onMetricClick,
}: FunnelStripProps) {

  return (
    <section aria-label="Funil de performance" className="h-full flex flex-col">
      {/*
        Card do funil — padding reduzido + min-height garantida.
        - Padding pequeno (p-2/p-3) maximiza a área útil interna.
        - `min-h-[300px]` (mobile) / `md:min-h-[420px]` (desktop) garante
          que o SVG cresça verticalmente acompanhando o card vizinho
          (QuickActionsRow empilhado), eliminando o "letterbox" branco.
        - Container interno usa `relative` + SVG `absolute inset-0` para
          que o SVG preencha exatamente 100% do espaço disponível, sem
          depender de altura fixa em px.
      */}
      <div className="aff-card p-2 md:p-3 flex-1 flex flex-col min-h-[300px] md:min-h-[420px]">
        {/* Legenda com explicação de cada etapa do funil. Conteúdo principal é
            SVG, então as explicações ficam nesta barra superior compacta. */}
        <div className="flex items-center justify-between gap-2 px-2 pt-1 pb-2 text-[10px] uppercase tracking-wider text-[hsl(var(--aff-muted-foreground))]">
          <span className="flex items-center gap-1">
            Cliques
            <KpiInfoTooltip
              label="Cliques"
              hint="Quantidade de cliques no seu link no período selecionado. Conta cada acesso registrado, independente de virar lead."
            />
          </span>
          <span className="flex items-center gap-1">
            Indicações
            <KpiInfoTooltip
              label="Indicações"
              hint="Leads cadastrados no período selecionado a partir dos seus cliques."
            />
          </span>
          <span className="flex items-center gap-1">
            Ganhos
            <KpiInfoTooltip
              label="Ganhos"
              hint="Indicações que foram convertidas em negócio fechado dentro do período. Conta pela data em que a conversão ocorreu."
            />
          </span>
          <span className="flex items-center gap-1">
            Retenção
            <KpiInfoTooltip
              label="Retenção"
              hint="Taxa de avanço entre as etapas do funil. Cliques→Indicações: % de cliques que viraram lead. Indicações→Ganhos: % de leads que converteram."
            />
          </span>
        </div>

        <div className="flex-1 flex items-center justify-center">
          {isLoading ? (
            <Skeleton className="h-full w-full rounded-xl" />
          ) : (
            <div className="relative h-full w-full">
              <Funnel3D
                clicks={clicks}
                leads={leads}
                conversions={conversions}
                r1={ratio(leads, clicks)}
                r2={ratio(conversions, leads)}
                onMetricClick={onMetricClick}
              />

            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// SVG: Funil 3D pastel — 3 anéis em perspectiva
// ===========================================================================
interface Funnel3DProps {
  clicks: number;
  leads: number;
  conversions: number;
  r1: number; // retenção cliques → leads
  r2: number; // retenção leads → ganhos
  /** v4.1.0 — Drill-down nos anéis clicáveis. */
  onMetricClick?: (metric: FunnelMetric) => void;
}

function Funnel3D({ clicks, leads, conversions, r1, r2, onMetricClick }: Funnel3DProps) {

  // ---------------------------------------------------------------------------
  // Geometria
  // ---------------------------------------------------------------------------
  // viewBox com proporção próxima ao card real (mais quadrada) — assim o
  // `preserveAspectRatio="xMidYMid meet"` gera margens mínimas em ambos
  // os eixos, fazendo o funil parecer maior dentro do mesmo card.
  const VB_W = 520;
  const VB_H = 440;
  // Funil perfeitamente centralizado no eixo X do viewBox.
  // Os textos de retenção (% à direita) cabem na área simétrica reservada
  // (ver `usable`) sem deslocar o funil.
  const cx = VB_W / 2;

  // Padding vertical — leve respiro nas bordas superior/inferior.
  const padY = 32;

  // Largura útil do topo do funil (~56% do viewBox). Equilíbrio entre
  // presença visual e respiro lateral para os rótulos de retenção (%).
  const usable = 290;

  // Larguras FIXAS dos anéis — formato visual do funil é constante,
  // independente de haver dados ou não. Apenas os números/porcentagens
  // exibidos mudam, evitando o funil "fino" quando todas as etapas são 0.
  const w1 = 1.0;   // topo (Cliques) — boca larga
  const w2 = 0.66;  // meio (Indicações)
  const w3 = 0.34;  // base (Ganhos)

  // 3 anéis com mesma altura
  const ringH = (VB_H - padY * 2) / 3;
  const y0 = padY;                // topo do anel 1
  const y1 = padY + ringH;        // junção 1↔2
  const y2 = padY + ringH * 2;    // junção 2↔3
  const y3 = padY + ringH * 3;    // base

  // Meia-largura (raio horizontal) em coordenadas SVG
  const rx = (w: number) => (usable * w) / 2;

  // Achatamento das elipses (perspectiva). Quanto menor, mais "topo de cima".
  const ellipseRy = 7;

  // Helper: gera o caminho de um anel (face frontal entre 2 elipses)
  // Topo (rxTop, yTop) → desce pelas laterais → base (rxBot, yBot) → fecha pela elipse de baixo
  const ringPath = (
    yTop: number,
    yBot: number,
    rTop: number,
    rBot: number,
  ) => {
    return [
      `M ${cx - rTop} ${yTop}`,
      `L ${cx - rBot} ${yBot}`,
      // metade inferior da elipse de baixo (curva para frente)
      `A ${rBot} ${ellipseRy} 0 0 0 ${cx + rBot} ${yBot}`,
      `L ${cx + rTop} ${yTop}`,
      // metade superior da elipse de cima (curva para trás — fecha)
      `A ${rTop} ${ellipseRy} 0 0 1 ${cx - rTop} ${yTop}`,
      'Z',
    ].join(' ');
  };

  // Conteúdo textual dos anéis
  const rings = [
    {
      key: 'clicks',
      label: 'CLIQUES',
      value: fmtInt(clicks),
      yMid: y0 + ringH / 2,
      gradient: 'g-pastel-lavender',
      labelColor: 'hsl(225 25% 35%)',
      valueColor: 'hsl(225 35% 22%)',
    },
    {
      key: 'leads',
      label: 'INDICAÇÕES',
      value: fmtInt(leads),
      yMid: y1 + ringH / 2,
      gradient: 'g-pastel-indigo',
      labelColor: 'hsl(245 35% 28%)',
      valueColor: 'hsl(245 50% 18%)',
    },
    {
      key: 'gains',
      label: 'GANHOS',
      value: fmtInt(conversions),
      yMid: y2 + ringH / 2,
      gradient: 'g-pastel-mint',
      labelColor: 'hsl(155 35% 24%)',
      valueColor: 'hsl(155 45% 16%)',
    },
  ];

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label="Funil de performance"
      // Ocupa 100% do container pai (que é `relative h-full w-full`).
      // Sem altura fixa em px → escala fluidamente quando o card cresce.
      className="absolute inset-0 h-full w-full block"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* ===== Gradientes pastel verticais ===== */}
        <linearGradient id="g-pastel-lavender" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F1F3FB" />
          <stop offset="100%" stopColor="#DDE3F2" />
        </linearGradient>
        <linearGradient id="g-pastel-indigo" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D6D5FF" />
          <stop offset="100%" stopColor="#A6A4F5" />
        </linearGradient>
        <linearGradient id="g-pastel-mint" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#CFEFDF" />
          <stop offset="100%" stopColor="#8FD9B6" />
        </linearGradient>

        {/* Highlight lateral esquerdo (brilho 3D suave) */}
        <linearGradient id="g-highlight" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="35%" stopColor="#FFFFFF" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Sombra interna na base de cada anel (escurece sutilmente) */}
        <linearGradient id="g-inner-shadow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.08" />
        </linearGradient>

        {/* Filtro de sombra projetada sob o funil */}
        <filter id="f-drop" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="0" dy="3" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.18" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ===== Sombra projetada sob o funil ===== */}
      <ellipse
        cx={cx}
        cy={y3 + 8}
        rx={rx(w3) * 1.4}
        ry={4}
        fill="rgba(15,23,42,0.12)"
      />

      {/* ===== Anel 1: Cliques ===== */}
      <g filter="url(#f-drop)">
        <path
          d={ringPath(y0, y1, rx(w1), rx(w2))}
          fill="url(#g-pastel-lavender)"
        />
        {/* Highlight lateral */}
        <path
          d={ringPath(y0, y1, rx(w1), rx(w2))}
          fill="url(#g-highlight)"
        />
        {/* Sombra interna */}
        <path
          d={ringPath(y0, y1, rx(w1), rx(w2))}
          fill="url(#g-inner-shadow)"
        />
        {/* Elipse do topo (boca do funil) — destaque sutil */}
        <ellipse
          cx={cx}
          cy={y0}
          rx={rx(w1)}
          ry={ellipseRy}
          fill="rgba(255,255,255,0.5)"
          stroke="hsl(225 20% 82%)"
          strokeWidth={0.6}
        />
      </g>

      {/* ===== Anel 2: Leads ===== */}
      <g filter="url(#f-drop)">
        <path
          d={ringPath(y1, y2, rx(w2), rx(w3))}
          fill="url(#g-pastel-indigo)"
        />
        <path
          d={ringPath(y1, y2, rx(w2), rx(w3))}
          fill="url(#g-highlight)"
        />
        <path
          d={ringPath(y1, y2, rx(w2), rx(w3))}
          fill="url(#g-inner-shadow)"
        />
        {/* Elipse de junção (topo do anel 2) */}
        <ellipse
          cx={cx}
          cy={y1}
          rx={rx(w2)}
          ry={ellipseRy}
          fill="rgba(0,0,0,0.08)"
        />
      </g>

      {/* ===== Anel 3: Ganhos ===== */}
      <g filter="url(#f-drop)">
        <path
          d={ringPath(y2, y3, rx(w3), rx(w3) * 0.72)}
          fill="url(#g-pastel-mint)"
        />
        <path
          d={ringPath(y2, y3, rx(w3), rx(w3) * 0.72)}
          fill="url(#g-highlight)"
        />
        <path
          d={ringPath(y2, y3, rx(w3), rx(w3) * 0.72)}
          fill="url(#g-inner-shadow)"
        />
        {/* Elipse de junção (topo do anel 3) */}
        <ellipse
          cx={cx}
          cy={y2}
          rx={rx(w3)}
          ry={ellipseRy}
          fill="rgba(0,0,0,0.08)"
        />
        {/* Elipse da base inferior (fundo fechado) */}
        <ellipse
          cx={cx}
          cy={y3}
          rx={rx(w3) * 0.72}
          ry={ellipseRy * 0.7}
          fill="hsl(155 40% 38%)"
          opacity={0.35}
        />
      </g>

      {/* ===== Texto centralizado dentro de cada anel ===== */}
      {rings.map((ring) => (
        <g key={ring.key}>
          <text
            x={cx}
            y={ring.yMid - 4}
            textAnchor="middle"
            fontSize={9}
            fontWeight={700}
            letterSpacing="1.8"
            fill={ring.labelColor}
          >
            {ring.label}
          </text>
          <text
            x={cx}
            y={ring.yMid + 18}
            textAnchor="middle"
            fontSize={22}
            fontWeight={800}
            fill={ring.valueColor}
            style={{ fontFeatureSettings: '"tnum" 1' }}
          >
            {ring.value}
          </text>
        </g>
      ))}

      {/* ===== Áreas clicáveis (drill-down) — sobrepostas aos anéis =====
          Apenas "Indicações" (anel 2) e "Ganhos" (anel 3) possuem listagem
          auditável. Renderizadas por último para ficarem acima dos textos. */}
      {onMetricClick && (
        <>
          <path
            d={ringPath(y1, y2, rx(w2), rx(w3))}
            fill="transparent"
            role="button"
            tabIndex={0}
            aria-label="Ver listagem de indicações"
            className="cursor-pointer focus:outline-none"
            onClick={() => onMetricClick('referrals')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onMetricClick('referrals');
              }
            }}
          />
          <path
            d={ringPath(y2, y3, rx(w3), rx(w3) * 0.72)}
            fill="transparent"
            role="button"
            tabIndex={0}
            aria-label="Ver listagem de ganhos"
            className="cursor-pointer focus:outline-none"
            onClick={() => onMetricClick('conversions')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onMetricClick('conversions');
              }
            }}
          />
        </>
      )}

      {/* ===== Retenção entre etapas — texto puro à direita ===== */}

      <RetentionText
        x={cx + rx(w2) + 22}
        y={y1 + 4}
        pct={r1}
      />
      <RetentionText
        x={cx + rx(w3) + 22}
        y={y2 + 4}
        pct={r2}
      />
    </svg>
  );
}

// ===========================================================================
// Retenção: seta + texto puro (sem fundo, alta legibilidade editorial)
// ===========================================================================
interface RetentionTextProps {
  x: number;
  y: number;
  pct: number;
}

function RetentionText({ x, y, pct }: RetentionTextProps) {
  return (
    <g>
      {/* Pequena seta para baixo desenhada em SVG (não ícone) */}
      <path
        d={`M ${x} ${y - 8} L ${x + 4} ${y - 2} L ${x + 8} ${y - 8}`}
        stroke="hsl(var(--aff-muted-foreground))"
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={0.7}
      />
      <text
        x={x + 14}
        y={y}
        fontSize={12}
        fontWeight={600}
        fill="hsl(var(--aff-muted-foreground))"
        style={{ fontFeatureSettings: '"tnum" 1' }}
      >
        {pct.toFixed(1)}%
      </text>
    </g>
  );
}
